import { useQuery } from '@tanstack/react-query';
import { backendServices } from 'common';

/**
 * Resolves the sender/receiver profile ids required by POST /Document/Draft
 * on a SIM submission:
 *   - SenderProfileId   -> supplier's Sales profile
 *   - ReceiverProfileId -> buyer's Procurement profile
 *
 * Mirrors supplier-portal's SIM branch in DocumentCreationThunk, which
 * takes `profiles[0].Id` for each side.
 */
export function useSimSubmissionProfiles({
    supplierCompanyId,
    buyerCompanyId,
    enabled = true,
}: {
    supplierCompanyId: number | null;
    buyerCompanyId: number | null;
    enabled?: boolean;
}) {
    const senderQuery = useQuery(
        ['SimSenderProfile', supplierCompanyId],
        async () => {
            const api = new backendServices.Apis.ProfileApi();
            const res = await api.getProfileSearch({
                profileType: backendServices.ViewModels.ProfileType.Sales,
                companyId: supplierCompanyId!,
            });
            return res.data[0]?.Id ?? null;
        },
        {
            enabled: enabled && supplierCompanyId != null,
            staleTime: Infinity,
            cacheTime: Infinity,
        }
    );

    const receiverQuery = useQuery(
        ['SimReceiverProfile', buyerCompanyId],
        async () => {
            const api = new backendServices.Apis.ProfileApi();
            const res = await api.getProfileSearch({
                profileType: backendServices.ViewModels.ProfileType.Procurement,
                companyId: buyerCompanyId!,
            });
            return res.data[0]?.Id ?? null;
        },
        {
            enabled: enabled && buyerCompanyId != null,
            staleTime: Infinity,
            cacheTime: Infinity,
        }
    );

    return {
        senderProfileId: senderQuery.data ?? null,
        receiverProfileId: receiverQuery.data ?? null,
        isLoading:
            senderQuery.isLoading || senderQuery.isFetching || receiverQuery.isLoading || receiverQuery.isFetching,
        isError: senderQuery.isError || receiverQuery.isError,
    };
}
